import React, { useState, useEffect } from 'react';
import { CardContent, Box, Alert } from '@mui/material';
import { InvestorOpportunitySummaryDTO, OpportunityInterestStatus } from '@inversor-hub/types';
import WarningIcon from '@mui/icons-material/Warning';
import { OpportunityTitle } from './OpportunityTitle.tsx';
import { PSICityInfo } from './PSICityInfo.tsx';
import { FinancialInfo } from './FinancialInfo.tsx';
import TooltipStatus, { TooltipStatusProps } from '@/components/ui/TooltipStatus.tsx';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import CancelIcon from '@mui/icons-material/Cancel';
import { calculateRemainingSelectedTime } from '@/features/opportunities/utils.ts';

interface OpportunityCardContentProps {
  opportunity: InvestorOpportunitySummaryDTO;
  children?: React.ReactNode;
  showStatus?: boolean;
}

const getStatusConfig = (
  status: OpportunityInterestStatus,
  interestUpdatedAt?: string
): {
  label: string;
  tooltip: string;
  color: TooltipStatusProps['color'];
  icon: React.ReactNode;
} | null => {
  const baseConfig = {
    PENDING: {
      label: 'Solicitud en revisión',
      icon: <EmailIcon fontSize="small" />,
      color: 'PENDING' as const,
      tooltip: 'El PSI está evaluando tu perfil y te notificará cuando haya novedades.',
    },
    SELECTED: {
      label: 'Seleccionado',
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'MAIN' as const,
      tooltip:
        'Has sido seleccionado. El siguiente paso es firmar el contrato para reservar tu inversión.',
    },
    RESERVED: {
      label: 'Reservada',
      icon: <LockIcon fontSize="small" />,
      color: 'SUCCESS' as const,
      tooltip:
        'La oportunidad está reservada a tu nombre. Accede a la inversión para ver los detalles.',
    },
    REJECTED: {
      label: 'No seleccionado',
      icon: <CancelIcon fontSize="small" />,
      color: 'REJECTED' as const,
      tooltip:
        'En esta ocasión no has sido seleccionado. Te animamos a explorar otras oportunidades disponibles.',
    },
    REMOVED: {
      label: 'Interés eliminado',
      icon: <CancelIcon fontSize="small" />,
      color: 'REJECTED' as const,
      tooltip: 'Has eliminado el interés de esta oportunidad.',
    },
  };

  const config = baseConfig[status];

  // Add countdown to SELECTED status label
  if (status === 'SELECTED' && interestUpdatedAt) {
    const timeRemaining = calculateRemainingSelectedTime(interestUpdatedAt);
    if (timeRemaining) {
      return {
        ...config,
        label: `Seleccionado · ${timeRemaining.text} restantes`,
      };
    }
  }

  return config;
};

export const OpportunityCardContent: React.FC<OpportunityCardContentProps> = ({
  opportunity,
  children,
  showStatus = false,
}) => {
  const [statusConfig, setStatusConfig] = useState(() =>
    opportunity.interestStatus
      ? getStatusConfig(opportunity.interestStatus, opportunity.interestUpdatedAt)
      : null
  );

  useEffect(() => {
    if (!opportunity.interestStatus || opportunity.interestStatus !== 'SELECTED') return;

    const interval = setInterval(() => {
      setStatusConfig(getStatusConfig(opportunity.interestStatus!, opportunity.interestUpdatedAt));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [opportunity.interestStatus, opportunity.interestUpdatedAt]);

  return (
    <CardContent
      sx={{
        flexGrow: 1,
        p: 2,
        pb: 1.5,
        display: 'flex',
        flexDirection: 'column',
        '&:last-child': {
          pb: 2,
        },
      }}
    >
      {/* Status Badge */}
      {showStatus && statusConfig && (
        <Box sx={{ mb: 1.5 }}>
          <TooltipStatus
            label={statusConfig.label}
            tooltip={statusConfig.tooltip}
            color={statusConfig.color}
            icon={statusConfig.icon}
          />
        </Box>
      )}

      {/* Title */}
      <OpportunityTitle title={opportunity.title} maxLines={1} />

      {/* PSI and Location */}
      <Box sx={{ mb: 1 }}>
        <PSICityInfo
          psiName={opportunity.psi.name}
          location={opportunity.city ?? opportunity.province}
        />
      </Box>

      {/* Room Sale Warning */}
      {opportunity.type === 'ROOM_SALE' && (
        <Box sx={{ mb: 1.5 }}>
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="small" />}
            sx={{
              py: 0.5,
              px: 1,
              fontSize: '0.75rem',
              '& .MuiAlert-message': {
                fontSize: '0.75rem',
                fontWeight: 500,
              },
            }}
          >
            Atención: Se trata de una habitación, no de un inmueble completo
          </Alert>
        </Box>
      )}

      {/* Financial Metrics */}
      <FinancialInfo
        grossReturnRental={opportunity.grossReturn}
        negotiatedPrice={opportunity.negotiatedPrice}
        previousPrice={opportunity.previousPrice}
      />

      {/* Spacer to push buttons to bottom */}
      <Box sx={{ flexGrow: 1 }} />

      {children}
    </CardContent>
  );
};
